import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, Minus, MapPin, AlertTriangle } from 'lucide-react';
import { formatTime, formatHoursMinutes, formatTimeString12Hour } from '../../../utils/dateUtils';
import { LeaveTypeBadge } from '../../UI/LeaveTypeBadge';
import { InfoBadge } from '../../UI/InfoBadge';

/** SGT date key (YYYY-MM-DD) from an ISO timestamp already serialised in SGT. */
function getEventDateKey(timestamp) {
  if (!timestamp) return null;
  return String(timestamp).slice(0, 10);
}

/**
 * Cross-midnight clock-outs are bridged onto the clock-in day server-side.
 * Compare event calendar date to the parent day to surface the +1d cue HR expects
 * (same pattern as Adjust & Lock / Attendance Daily).
 */
function isNextDayClockEvent(evt, dayDate) {
  if (!evt || evt.event_type !== 'clock_out' || !dayDate) return false;
  const evtDate = getEventDateKey(evt.timestamp);
  return Boolean(evtDate && evtDate > dayDate);
}

function dayHasNextDayClockOut(day) {
  return (day?.clock_events || []).some((evt) => isNextDayClockEvent(evt, day.date));
}

function NextDayBadge({ className = '' }) {
  return (
    <InfoBadge
      color="amber"
      label="+1d"
      size="xs"
      className={className}
      title="Clock-out was in the early morning of the next calendar day"
    />
  );
}

/** Full-width event grid — avoids table-fixed void between Location and Geofence. */
function eventGridClass(isWorker) {
  return isWorker
    ? 'grid w-full gap-x-3 items-start grid-cols-[6.5rem_minmax(5.5rem,auto)_minmax(0,1.15fr)_minmax(0,1.85fr)_5.5rem_4rem_3rem_4.5rem]'
    : 'grid w-full gap-x-3 items-start grid-cols-[6.5rem_minmax(5.5rem,auto)_minmax(0,1.15fr)_minmax(0,1.85fr)_5.5rem_4rem]';
}

/** DS Attendance Table Color Scheme — parent-row backgrounds (no opacity washout). */
const DAY_ROW_BG = {
  on_site:            'bg-white hover:bg-gray-50',
  work_from_home:     'bg-white hover:bg-gray-50',
  external_activity:  'bg-white hover:bg-gray-50',
  on_course:          'bg-white hover:bg-gray-50',
  on_leave:           'bg-amber-50 hover:bg-amber-100',
  nightwork:          'bg-white hover:bg-gray-50',
  nightwork_rest_day: 'bg-yellow-50',
  unaccounted:        'bg-red-50 hover:bg-red-100',
  non_working_day:    'bg-gray-100 hover:bg-gray-200',
  public_holiday:     'bg-purple-50 hover:bg-purple-100',
};

const DAY_STATUS_CHIP = {
  on_site:            { label: 'Present',     classes: 'bg-green-100 text-green-700' },
  work_from_home:     { label: 'WFH',         classes: 'bg-indigo-100 text-indigo-700' },
  external_activity:  { label: 'External',    classes: 'bg-cyan-100 text-cyan-700' },
  on_course:          { label: 'On Course',   classes: 'bg-violet-100 text-violet-700' },
  on_leave:           { label: 'On Leave',    classes: 'bg-amber-100 text-amber-700' },
  nightwork:          { label: 'Nightwork',   classes: 'bg-slate-100 text-slate-700' },
  nightwork_rest_day: { label: 'Recovery',    classes: 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/30' },
  unaccounted:        { label: 'Absent',      classes: 'bg-red-100 text-red-700' },
  non_working_day:    { label: 'Off',         classes: 'bg-gray-100 text-gray-500 ring-1 ring-inset ring-gray-500/25' },
  public_holiday:     { label: 'PH',          classes: 'bg-purple-100 text-purple-700' },
};

function getDayRowBg(status) {
  return DAY_ROW_BG[status] || DAY_ROW_BG.non_working_day;
}

function GeofenceIndicator({ value, nearestProjectName, nearestProjectDistanceM }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1" title="Within geofence">
        <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
        <span className="sr-only">Within geofence</span>
      </span>
    );
  }
  if (value === false && nearestProjectName) {
    const dist = nearestProjectDistanceM != null ? `${Math.round(nearestProjectDistanceM)}m` : '';
    return (
      <span
        className="inline-flex items-center gap-1 max-w-full"
        title={`At ${nearestProjectName}${dist ? ` (${dist})` : ''}`}
      >
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" aria-hidden="true" />
        <span className="text-xs text-amber-700 font-medium truncate max-w-[120px]">
          {nearestProjectName}
        </span>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1" title="Outside geofence">
        <XCircle className="w-4 h-4 text-red-500" aria-hidden="true" />
        <span className="sr-only">Outside geofence</span>
      </span>
    );
  }
  return <Minus className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />;
}

function DistanceCell({ distanceM, withinGeofence }) {
  if (distanceM == null) {
    return <span className="text-gray-400">&mdash;</span>;
  }
  const rounded = `${Math.round(distanceM)}m`;
  const isOut = withinGeofence === false;
  const isFar = distanceM >= 500;
  if (isOut || isFar) {
    return (
      <span className={`font-medium tabular-nums ${isOut ? 'text-red-600' : 'text-amber-600'}`}>
        {rounded}
      </span>
    );
  }
  return <span className="text-gray-700 tabular-nums">{rounded}</span>;
}

function AttendanceStatusChip({ status, leaveTypeName }) {
  if (status === 'on_leave' && leaveTypeName) {
    return <LeaveTypeBadge leaveTypeName={leaveTypeName} size="xs" />;
  }
  const cfg = DAY_STATUS_CHIP[status] || DAY_STATUS_CHIP.non_working_day;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium not-italic whitespace-nowrap ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
}

function EventTypeChip({ eventType }) {
  const isIn = eventType === 'clock_in';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${
        isIn
          ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
          : 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20'
      }`}
    >
      {isIn ? 'Clock In' : 'Clock Out'}
    </span>
  );
}

function enrichEventsWithInheritedProject(events) {
  if (!events?.length) return events;
  let lastProject = null;
  return events.map(evt => {
    if (evt.event_type === 'clock_in' && evt.project_name) {
      lastProject = { project_name: evt.project_name, project_code: evt.project_code };
    }
    if (evt.event_type === 'clock_out' && !evt.project_name && lastProject) {
      return { ...evt, project_name: lastProject.project_name, project_code: lastProject.project_code, inherited_project: true };
    }
    return evt;
  });
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateCompactInline(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return { label: dateStr, weekday: '' };
  const day = d.getDate();
  const mon = MONTH_SHORT[d.getMonth()];
  const weekday = WEEKDAY_SHORT[d.getDay()];
  return { label: `${day} ${mon}`, weekday };
}

function EmptyValue() {
  return <span className="text-gray-400">&mdash;</span>;
}

function EventTimeDisplay({ evt, dayDate, align = 'center' }) {
  const nextDay = isNextDayClockEvent(evt, dayDate);
  const justify =
    align === 'right' ? 'justify-end'
    : align === 'left' ? 'justify-start'
    : 'justify-center';
  return (
    <span className={`flex w-full items-center gap-1.5 flex-wrap ${justify}`}>
      <span className="font-mono tabular-nums text-gray-900 font-medium whitespace-nowrap">
        {formatTime(evt.timestamp)}
      </span>
      {nextDay && <NextDayBadge />}
    </span>
  );
}

function ExpandedClockEvents({ events, isWorker, dayDate }) {
  const colCount = isWorker ? 9 : 7;
  const gridClass = eventGridClass(isWorker);

  if (!events?.length) {
    return (
      <tr>
        <td colSpan={colCount} className="!p-0 !border-t-0">
          <div className="bg-gray-50 border-b border-gray-200 border-l-4 border-l-gray-300 px-4 py-3 text-center text-xs text-gray-500">
            No clock events recorded for this day
          </div>
        </td>
      </tr>
    );
  }

  const enrichedEvents = enrichEventsWithInheritedProject(events);

  return (
    <tr>
      <td colSpan={colCount} className="!p-0 !border-t-0">
        <div className="bg-gray-50 border-b border-gray-200 border-l-4 border-l-gray-300">
          <div className="px-4 py-2">
            <div
              className={`${gridClass} px-1 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider`}
              role="row"
            >
              <div>Event</div>
              <div className="text-center">Time</div>
              <div>Project</div>
              <div>Location</div>
              <div className="text-center">Geofence</div>
              <div className="text-right">Distance</div>
              {isWorker && (
                <>
                  <div className="text-center">OT</div>
                  <div className="text-right">OT Hrs</div>
                </>
              )}
            </div>
            <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white overflow-hidden">
              {enrichedEvents.map((evt, i) => (
                <div key={i} className={`${gridClass} px-3 py-2.5 text-sm`} role="row">
                  <div>
                    <EventTypeChip eventType={evt.event_type} />
                  </div>
                  <div className="text-center">
                    <EventTimeDisplay evt={evt} dayDate={dayDate} />
                  </div>
                  <div className="min-w-0">
                    {evt.project_name ? (
                      <span
                        title={evt.project_code ? `${evt.project_name} (${evt.project_code})` : evt.project_name}
                        className={`block truncate ${evt.inherited_project ? 'text-gray-500 italic' : 'text-gray-900'}`}
                      >
                        {evt.project_name}
                        {evt.inherited_project && (
                          <span className="text-xs ml-1 text-gray-400 not-italic">(from clock-in)</span>
                        )}
                      </span>
                    ) : (
                      <EmptyValue />
                    )}
                  </div>
                  <div className="min-w-0 text-gray-700">
                    {evt.address ? (
                      <span className="flex items-start gap-1.5" title={evt.address}>
                        <MapPin className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="line-clamp-2 break-words">{evt.address}</span>
                      </span>
                    ) : (
                      <span className="text-gray-500 italic">Pending</span>
                    )}
                  </div>
                  <div className="flex items-center justify-center">
                    <GeofenceIndicator
                      value={evt.within_geofence}
                      nearestProjectName={evt.nearest_project_name}
                      nearestProjectDistanceM={evt.nearest_project_distance_m}
                    />
                  </div>
                  <div className="text-right font-mono">
                    <DistanceCell distanceM={evt.distance_m} withinGeofence={evt.within_geofence} />
                  </div>
                  {isWorker && (
                    <>
                      <div className="text-center">
                        {evt.is_overtime ? (
                          <span className="text-amber-700 font-medium">Yes</span>
                        ) : (
                          <EmptyValue />
                        )}
                      </div>
                      <div className="text-right font-mono tabular-nums">
                        {evt.overtime_hours > 0 ? (
                          <span className="text-amber-700 font-medium">{formatHoursMinutes(evt.overtime_hours)}</span>
                        ) : (
                          <EmptyValue />
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function MobileClockEvents({ events, isWorker, dayDate }) {
  if (!events?.length) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200 text-center text-xs text-gray-500">
        No clock events recorded for this day
      </div>
    );
  }

  const enrichedEvents = enrichEventsWithInheritedProject(events);

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
      {enrichedEvents.map((evt, i) => (
        <div key={i} className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <EventTypeChip eventType={evt.event_type} />
            <EventTimeDisplay evt={evt} dayDate={dayDate} align="right" />
          </div>
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-500 flex-shrink-0 text-xs">Project</span>
            <span className="text-right truncate min-w-0 text-sm">
              {evt.project_name ? (
                <span
                  title={evt.project_code ? `${evt.project_name} (${evt.project_code})` : evt.project_name}
                  className={evt.inherited_project ? 'text-gray-500 italic' : 'text-gray-900'}
                >
                  {evt.project_name}
                </span>
              ) : (
                <EmptyValue />
              )}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-500 flex-shrink-0 text-xs">Location</span>
            <span className="text-right min-w-0 text-sm text-gray-700 line-clamp-2" title={evt.address || undefined}>
              {evt.address || <span className="text-gray-500 italic">Pending</span>}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-500 flex-shrink-0 text-xs">Geofence</span>
            <span className="text-right min-w-0 text-sm inline-flex items-center justify-end gap-1.5">
              <GeofenceIndicator
                value={evt.within_geofence}
                nearestProjectName={evt.nearest_project_name}
                nearestProjectDistanceM={evt.nearest_project_distance_m}
              />
              <span className="font-mono">
                <DistanceCell distanceM={evt.distance_m} withinGeofence={evt.within_geofence} />
              </span>
            </span>
          </div>
          {isWorker && evt.is_overtime && (
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-500 flex-shrink-0 text-xs">Overtime</span>
              <span className="text-right truncate min-w-0 text-sm text-amber-700 font-medium tabular-nums">
                {evt.overtime_hours > 0 ? formatHoursMinutes(evt.overtime_hours) : 'Yes'}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function dayInteractiveClasses(hasEvents) {
  if (!hasEvents) return '';
  return [
    'cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-inset',
  ].join(' ');
}

export default function DailyDetailView({
  dailyStatus,
  isWorker = false,
  expandedDays: controlledExpanded,
  onExpandedChange,
}) {
  const [internalExpanded, setInternalExpanded] = useState(new Set());
  const isControlled = controlledExpanded !== undefined;
  const expandedDays = isControlled ? controlledExpanded : internalExpanded;
  const setExpandedDays = isControlled
    ? (updater) => {
        const next = typeof updater === 'function' ? updater(controlledExpanded) : updater;
        onExpandedChange?.(next);
      }
    : setInternalExpanded;

  const toggleDay = useCallback((dateStr) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  }, [setExpandedDays]);

  const handleKeyDown = (e, dateStr, hasEvents) => {
    if (!hasEvents) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDay(dateStr);
    }
  };

  const sortedDays = useMemo(() => {
    if (!dailyStatus?.length) return [];
    return [...dailyStatus].sort((a, b) => a.date.localeCompare(b.date));
  }, [dailyStatus]);

  if (!sortedDays.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-sm">No daily data available for this month</p>
      </div>
    );
  }

  return (
    <>
    {/* Mobile card view */}
    <div className="md:hidden space-y-3">
      {sortedDays.map((day) => {
        const isExpanded = expandedDays.has(day.date);
        const hasEvents = day.clock_events?.length > 0;
        const { label: dateLabel, weekday } = formatDateCompactInline(day.date);
        const totalOt = (day.clock_events || []).reduce((s, e) => s + (e.overtime_hours || 0), 0);
        const rowBg = getDayRowBg(day.composite_status);

        return (
          <div
            key={day.date}
            onClick={() => hasEvents && toggleDay(day.date)}
            onKeyDown={(e) => handleKeyDown(e, day.date, hasEvents)}
            tabIndex={hasEvents ? 0 : undefined}
            role={hasEvents ? 'button' : undefined}
            aria-expanded={hasEvents ? isExpanded : undefined}
            className={[
              'rounded-xl border border-gray-200 p-4 transition-colors',
              rowBg,
              dayInteractiveClasses(hasEvents),
            ].filter(Boolean).join(' ')}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{dateLabel}</p>
                <p className="text-xs text-gray-600">{weekday}</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <AttendanceStatusChip status={day.composite_status} leaveTypeName={day.leave_type_name} />
                {hasEvents && (
                  isExpanded
                    ? <ChevronUp className="w-4 h-4 text-gray-600" aria-hidden="true" />
                    : <ChevronDown className="w-4 h-4 text-gray-600" aria-hidden="true" />
                )}
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-600 flex-shrink-0">First In</span>
                <span className="text-gray-900 text-right truncate min-w-0 font-mono tabular-nums font-medium">
                  {day.first_clock_in ? formatTimeString12Hour(day.first_clock_in) : <EmptyValue />}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-600 flex-shrink-0">Last Out</span>
                <span className="text-right min-w-0 inline-flex items-center justify-end gap-1.5 font-mono tabular-nums font-medium text-gray-900">
                  {day.last_clock_out ? (
                    <>
                      <span>{formatTimeString12Hour(day.last_clock_out)}</span>
                      {dayHasNextDayClockOut(day) && <NextDayBadge />}
                    </>
                  ) : (
                    <EmptyValue />
                  )}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-600 flex-shrink-0">Total Hours</span>
                <span className="text-gray-900 text-right truncate min-w-0 font-mono tabular-nums font-medium">
                  {day.total_hours != null ? formatHoursMinutes(day.total_hours) : <EmptyValue />}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-600 flex-shrink-0">Extra Hrs</span>
                <span className="text-right truncate min-w-0 font-mono tabular-nums">
                  {day.beyond_schedule_hours > 0 ? (
                    <span className={isWorker ? 'text-sky-700 font-semibold' : 'text-amber-700 font-semibold'}>
                      {formatHoursMinutes(day.beyond_schedule_hours)}
                    </span>
                  ) : (
                    <EmptyValue />
                  )}
                </span>
              </div>
              {isWorker && (
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-gray-600 flex-shrink-0">OT Hours</span>
                  <span className="text-right truncate min-w-0 font-mono tabular-nums">
                    {totalOt > 0 ? (
                      <span className="text-amber-700 font-semibold">{formatHoursMinutes(totalOt)}</span>
                    ) : (
                      <EmptyValue />
                    )}
                  </span>
                </div>
              )}
            </div>

            {isExpanded && hasEvents && (
              <MobileClockEvents events={day.clock_events} isWorker={isWorker} dayDate={day.date} />
            )}
          </div>
        );
      })}
    </div>

    {/* Desktop table view */}
    <div className="hidden md:block overflow-x-auto -mx-1">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="table-header sticky top-0 z-10">
          <tr>
            <th className="table-header-cell w-10 !px-3" scope="col">
              <span className="sr-only">Expand</span>
            </th>
            <th className="table-header-cell text-right" scope="col">Date</th>
            <th className="table-header-cell text-center" scope="col">Status</th>
            <th className="table-header-cell text-center" scope="col">First In</th>
            <th className="table-header-cell text-center" scope="col">Last Out</th>
            <th className="table-header-cell text-right" scope="col">Total Hours</th>
            <th className="table-header-cell text-right" scope="col">Extra Hrs</th>
            {isWorker && (
              <>
                <th className="table-header-cell text-center" scope="col">OT</th>
                <th className="table-header-cell text-right" scope="col">OT Hours</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="table-body">
          {sortedDays.map((day) => {
            const isExpanded = expandedDays.has(day.date);
            const hasEvents = day.clock_events?.length > 0;
            const { label: dateLabel, weekday } = formatDateCompactInline(day.date);
            const rowBg = getDayRowBg(day.composite_status);
            const totalOt = (day.clock_events || []).reduce((s, e) => s + (e.overtime_hours || 0), 0);

            return (
              <React.Fragment key={day.date}>
                <tr
                  onClick={() => hasEvents && toggleDay(day.date)}
                  onKeyDown={(e) => handleKeyDown(e, day.date, hasEvents)}
                  tabIndex={hasEvents ? 0 : undefined}
                  role={hasEvents ? 'button' : undefined}
                  aria-expanded={hasEvents ? isExpanded : undefined}
                  className={[
                    rowBg,
                    'transition-colors',
                    dayInteractiveClasses(hasEvents),
                    isExpanded ? '!border-b-0' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td className="table-cell !px-3 w-10 text-center">
                    {hasEvents && (
                      isExpanded
                        ? <ChevronUp className="w-4 h-4 text-gray-600 inline" aria-hidden="true" />
                        : <ChevronDown className="w-4 h-4 text-gray-600 inline" aria-hidden="true" />
                    )}
                  </td>
                  <td className="table-cell text-right text-sm">
                    <span className="font-semibold text-gray-900">{dateLabel}</span>
                    <span className="text-gray-600 ml-1.5 text-xs">{weekday}</span>
                  </td>
                  <td className="table-cell text-center">
                    <div className="flex items-center justify-center">
                      <AttendanceStatusChip status={day.composite_status} leaveTypeName={day.leave_type_name} />
                    </div>
                  </td>
                  <td className="table-cell text-center text-sm font-mono tabular-nums text-gray-900 font-medium">
                    {day.first_clock_in ? formatTimeString12Hour(day.first_clock_in) : <EmptyValue />}
                  </td>
                  <td className="table-cell text-center text-sm font-mono tabular-nums text-gray-900 font-medium">
                    {day.last_clock_out ? (
                      <span className="inline-flex items-center justify-center gap-1.5 flex-wrap">
                        <span>{formatTimeString12Hour(day.last_clock_out)}</span>
                        {dayHasNextDayClockOut(day) && <NextDayBadge />}
                      </span>
                    ) : (
                      <EmptyValue />
                    )}
                  </td>
                  <td className="table-cell text-right text-sm font-mono tabular-nums text-gray-900 font-medium">
                    {day.total_hours != null ? formatHoursMinutes(day.total_hours) : <EmptyValue />}
                  </td>
                  <td className="table-cell text-right text-sm font-mono tabular-nums">
                    {day.beyond_schedule_hours > 0 ? (
                      <span className={isWorker ? 'text-sky-700 font-semibold' : 'text-amber-700 font-semibold'}>
                        {formatHoursMinutes(day.beyond_schedule_hours)}
                      </span>
                    ) : (
                      <EmptyValue />
                    )}
                  </td>
                  {isWorker && (
                    <>
                      <td className="table-cell text-center text-sm">
                        {day.clock_events?.some(e => e.is_overtime) ? (
                          <span className="text-amber-700 font-semibold">Yes</span>
                        ) : (
                          <EmptyValue />
                        )}
                      </td>
                      <td className="table-cell text-right text-sm font-mono tabular-nums">
                        {totalOt > 0 ? (
                          <span className="text-amber-700 font-semibold">{formatHoursMinutes(totalOt)}</span>
                        ) : (
                          <EmptyValue />
                        )}
                      </td>
                    </>
                  )}
                </tr>

                {isExpanded && hasEvents && (
                  <ExpandedClockEvents
                    events={day.clock_events}
                    isWorker={isWorker}
                    dayDate={day.date}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}
